import React, { useMemo } from "react";
import { X, Upload, AlertTriangle, CheckCircle } from "lucide-react";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import CircularProgress from "@mui/material/CircularProgress";
import ExcelJS from "exceljs";
import styles from "./ImportPreviewModal.module.css";
import { CustomerDto } from "../dto/ImportCoworkingCustomerRow";
import { validateImportedCustomerRow } from "../common/importCustomerValidation";

interface ImportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  data: CustomerDto[];
  loading: boolean;
  fileName?: string;
}

const fieldLabels: Record<string, string> = {
  name: 'Nome',
  email: 'Email',
  phone: 'Telefone',
  companyDocument: 'CNPJ',
  postalBoxNumber: 'Caixa Postal',
  legalRepresentativeName: 'Representante',
  legalRepresentativeDocument: 'CPF Rep.',
  birthDate: 'Data Nasc.',
  address: 'Endereço',
  productName: 'Produto',
  startDate: 'Início',
  endDate: 'Término',
  affiliateId: 'Afiliado',
};

const EXPORT_FIELDS: (keyof CustomerDto)[] = [
  'name',
  'email',
  'phone',
  'companyDocument',
  'postalBoxNumber',
  'legalRepresentativeName',
  'legalRepresentativeDocument',
  'birthDate',
  'address',
  'productName',
  'startDate',
  'endDate',
  'affiliateId',
];

const ImportPreviewModal: React.FC<ImportPreviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  data,
  loading,
  fileName,
}) => {
  // Process data with validation
  const processedData = useMemo(() => {
    return data.map((row, index) => {
      const validation = validateImportedCustomerRow(row);

      return {
      ...row,
      id: index + 1,
      _errorFields: validation.fields,
      _errorMessages: validation.messages,
      };
    });
  }, [data]);

  // Count valid and invalid rows
  const stats = useMemo(() => {
    const valid = processedData.filter(row => row._errorFields.length === 0).length;
    const invalid = processedData.length - valid;
    return { total: processedData.length, valid, invalid };
  }, [processedData]);

  const invalidRows = useMemo(
    () => processedData.filter(row => row._errorFields.length > 0),
    [processedData],
  );

  const handleDownloadInvalidRows = async () => {
    if (invalidRows.length === 0) return;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'CoWonder';
    workbook.created = new Date();

    const worksheet = workbook.addWorksheet('Linhas com erro');
    worksheet.columns = [
      { header: 'Linha', key: 'line', width: 10 },
      ...EXPORT_FIELDS.map((field) => ({
        header: fieldLabels[field] ?? field,
        key: field,
        width: 24,
      })),
      { header: 'Erros de validação', key: 'validationErrors', width: 55 },
    ];

    invalidRows.forEach((row) => {
      const lineData = EXPORT_FIELDS.reduce<Record<string, unknown>>((acc, field) => {
        acc[field] = row[field] ?? '';
        return acc;
      }, {});

      const addedRow = worksheet.addRow({
        line: row.id,
        ...lineData,
        validationErrors: row._errorMessages.join(', '),
      });

      row._errorFields.forEach((field: string) => {
        const fieldIndex = EXPORT_FIELDS.findIndex((exportField) => exportField === field);
        if (fieldIndex === -1) return;

        const cell = addedRow.getCell(fieldIndex + 2);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFEE2E2' },
        };
        cell.font = {
          color: { argb: 'FF991B1B' },
          bold: true,
        };
      });

      const validationErrorsCell = addedRow.getCell(EXPORT_FIELDS.length + 2);
      validationErrorsCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFEE2E2' },
      };
      validationErrorsCell.font = {
        color: { argb: 'FF991B1B' },
        bold: true,
      };
    });

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDC2626' },
      };
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const safeBaseName = (fileName || 'importacao')
      .replace(/\.[^/.]+$/, '')
      .replace(/[^a-zA-Z0-9_-]+/g, '_');

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${safeBaseName}_linhas_com_erro.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Define columns for DataGrid
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'id',
      headerName: '#',
      width: 60,
      sortable: false,
    },
    {
      field: 'name',
      headerName: 'Nome Empresarial',
      width: 180,
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'email',
      headerName: 'Email *',
      width: 200,
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => {
        const isMissing = params.row._errorFields?.includes('email');
        return (
          <span className={isMissing ? styles.errorCell : undefined}>
            {params.value || (isMissing ? '(vazio)' : '-')}
          </span>
        );
      },
    },
    {
      field: 'phone',
      headerName: 'Telefone *',
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        const isMissing = params.row._errorFields?.includes('phone');
        return (
          <span className={isMissing ? styles.errorCell : undefined}>
            {params.value || (isMissing ? '(vazio)' : '-')}
          </span>
        );
      },
    },
    {
      field: 'legalRepresentativeName',
      headerName: 'Representante',
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        const isMissing = params.row._errorFields?.includes('legalRepresentativeName');
        return (
          <span className={isMissing ? styles.errorCell : undefined}>
            {params.value || (isMissing ? '(vazio)' : '-')}
          </span>
        );
      },
    },
    {
      field: 'legalRepresentativeDocument',
      headerName: 'CPF Rep.',
      width: 130,
    },
    {
      field: 'productName',
      headerName: 'Produto *',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const isMissing = params.row._errorFields?.includes('productName');
        return (
          <span className={isMissing ? styles.errorCell : undefined}>
            {params.value || (isMissing ? '(vazio)' : '-')}
          </span>
        );
      },
    },
    {
      field: 'startDate',
      headerName: 'Início *',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const isMissing = params.row._errorFields?.includes('startDate');
        return (
          <span className={isMissing ? styles.errorCell : undefined}>
            {params.value || (isMissing ? '(vazio)' : '-')}
          </span>
        );
      },
    },
    {
      field: 'endDate',
      headerName: 'Término *',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const isMissing = params.row._errorFields?.includes('endDate');
        return (
          <span className={isMissing ? styles.errorCell : undefined}>
            {params.value || (isMissing ? '(vazio)' : '-')}
          </span>
        );
      },
    },
  ], []);

  if (!isOpen) return null;

  const hasErrors = stats.invalid > 0;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            Pré-visualização da Importação
          </h2>
          <button className={styles.closeBtn} onClick={onClose} title="Fechar">
            <X size={24} />
          </button>
        </div>

        {/* Stats Bar */}
        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Arquivo:</span>
            <span className={styles.statValue}>{fileName || 'arquivo'}</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statLabel}>Total de linhas:</span>
            <span className={styles.statValue}>{stats.total}</span>
          </div>
          <div className={styles.statItem}>
            <CheckCircle size={16} color="#16a34a" />
            <span className={styles.statLabel}>Válidas:</span>
            <span className={`${styles.statValue} ${styles.statValueSuccess}`}>
              {stats.valid}
            </span>
          </div>
          {stats.invalid > 0 && (
            <div className={styles.statItem}>
              <AlertTriangle size={16} color="#dc2626" />
              <span className={styles.statLabel}>Com erros:</span>
              <span className={`${styles.statValue} ${styles.statValueError}`}>
                {stats.invalid}
              </span>
            </div>
          )}
        </div>

        {/* Data Grid */}
        <div className={styles.tableContainer}>
          <DataGrid
            rows={processedData}
            columns={columns}
            className={styles.dataGrid}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowClassName={(params) =>
              params.row._errorFields?.length > 0 ? styles.errorRow : styles.validRow
            }
            localeText={{
              noRowsLabel: 'Nenhum dado encontrado',
              footerRowSelected: (count) => `${count} linha(s) selecionada(s)`,
              footerTotalRows: 'Total de linhas:',
              MuiTablePagination: {
                labelRowsPerPage: 'Linhas por página:',
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`,
              },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f3ff',
                borderBottom: '2px solid #e5e7eb',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
                color: '#6b7280',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid #f3f4f6',
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '1px solid #e5e7eb',
                position: 'sticky',
                bottom: 0,
                backgroundColor: '#ffffff',
                zIndex: 2,
              },
            }}
          />
        </div>

        {/* Footer */}
        <div className={styles.modalFooter}>
          <div className={styles.footerInfo}>
            {hasErrors ? (
              <span style={{ color: '#dc2626' }}>
                <AlertTriangle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {stats.invalid} linha(s) com erro de validação serão ignoradas
                {' '}
                <button
                  type="button"
                  className={styles.downloadErrorRowsLink}
                  onClick={handleDownloadInvalidRows}
                  disabled={loading}
                  title="Baixar .xlsx com linhas inválidas"
                >
                  Baixar .xlsx com erros
                </button>
              </span>
            ) : (
              <span style={{ color: '#16a34a' }}>
                <CheckCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                Todas as linhas estão válidas
              </span>
            )}
          </div>
          <div className={styles.footerActions}>
            <button className={styles.cancelBtn} onClick={onClose} disabled={loading}>
              Cancelar
            </button>
            <button
              className={styles.confirmBtn}
              onClick={onConfirm}
              disabled={loading || stats.valid === 0}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} color="inherit" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Confirmar Importação ({stats.valid})
                </>
              )}
            </button>
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className={styles.loadingOverlay}>
            <CircularProgress size={40} style={{ color: '#7e22ce' }} />
            <span className={styles.loadingText}>Importando clientes...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportPreviewModal;

